/**
 * 선택한 CCTV 의 이미지 스테이지.
 *
 * 기존 RealtimeImageGallery 는 800px 고정 카드 안에 원본/ROI 이미지를 2열로
 * 늘어놓아, 화면이 커도 이미지는 그대로 작고 스크롤이 생겼다. 여기서는:
 *   - 부모가 준 높이를 다 쓰고, 이미지는 contain 으로 맞춘다.
 *   - 원본/ROI 결과는 탭으로 전환 — 같은 자리에서 비교된다.
 *   - 파일을 끌어다 놓으면 그 이미지로 대체 (업로드 버튼과 동일 동작).
 */
import React, { useEffect, useState } from 'react';
import { Box, Stack, Tab, Tabs, Typography, CircularProgress, Alert, useTheme } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { CctvConfig } from '../../../models/CctvTemplate';
import { consoleTokens } from '../../../styles/consoleTokens';

interface Props {
  cctv: CctvConfig | null;
  selectedCctv: string;
  images: Record<string, string> | null;
  imageUpdateKey: number;
  loading: boolean;
  error: string | null;
  onZoom: (src: string, title: string, alt: string) => void;
  onDropFile: (file: File) => void;
}

const CctvImageStage: React.FC<Props> = ({
  cctv,
  selectedCctv,
  images,
  imageUpdateKey,
  loading,
  error,
  onZoom,
  onDropFile,
}) => {
  const t = consoleTokens(useTheme().palette.mode);
  const types = cctv?.images.map((i) => i.type) ?? [];
  const [type, setType] = useState<string>(types[0] || '');
  const [dragOver, setDragOver] = useState(false);

  // CCTV 를 바꾸면 그 카메라가 가진 첫 이미지 종류로 되돌린다.
  useEffect(() => {
    if (types.length > 0 && !types.includes(type)) setType(types[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCctv, types.join(',')]);

  const current = cctv?.images.find((i) => i.type === type);
  const src = images?.[type] || '';

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) onDropFile(file);
  };

  return (
    <Box
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: t.panel,
        border: `1px solid ${dragOver ? t.accent : t.line}`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, borderBottom: `1px solid ${t.lineSoft}` }}>
        <Tabs
          value={type || false}
          onChange={(_, v) => setType(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 34, flex: 1, '& .MuiTab-root': { minHeight: 34, py: 0, fontSize: 13 } }}
        >
          {cctv?.images.map((img) => <Tab key={img.type} value={img.type} label={img.displayName} />)}
        </Tabs>
        <Typography sx={{ fontSize: 12, color: t.faint, fontFamily: t.mono, whiteSpace: 'nowrap' }}>
          {selectedCctv || '—'}
        </Typography>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', bgcolor: t.stage, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading ? (
          <CircularProgress size={40} />
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : !selectedCctv ? (
          <Typography sx={{ color: t.faint, fontSize: 14 }}>왼쪽에서 CCTV 를 고르거나 도면의 마커를 클릭하세요.</Typography>
        ) : src ? (
          <>
            <img
              key={`${type}-${imageUpdateKey}`}
              src={src}
              alt={`${selectedCctv} - ${type}`}
              onClick={() => onZoom(src, `${current?.displayName || type} - ${selectedCctv}`, `${selectedCctv} - ${type}`)}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', cursor: 'zoom-in', display: 'block' }}
            />
            <ZoomInIcon sx={{ position: 'absolute', top: 8, right: 8, color: t.stageInk, opacity: 0.6, fontSize: 20, pointerEvents: 'none' }} />
          </>
        ) : (
          <Typography sx={{ color: t.faint, fontSize: 14 }}>이미지를 불러올 수 없습니다. 이미지를 끌어다 놓아 대신 쓸 수 있습니다.</Typography>
        )}

        {dragOver && (
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.55)', color: t.stageInk, fontSize: 14, pointerEvents: 'none',
          }}>
            놓으면 이 이미지로 작업합니다
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CctvImageStage;
